import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import t from 'ember-intl/helpers/t';
import CaptionedLink from './captioned-link';

const resize = modifier(
  (element, positional) => {
    let resizeListener = function () {
      let rect = element.getBoundingClientRect();
      if (positional.length > 0) {
        let resized = positional[0];
        if (resized) {
          resized(rect.width, rect.height);
        }
      }
    };
    if (element) {
      resizeListener();
      window.addEventListener('resize', resizeListener);
    }
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  },
  { eager: false },
);

// Tool selection panel
export default class ToolSelection extends Component {
  currentElement = null;
  @tracked width;
  @tracked height;
  @tracked windowWidth;
  @tracked windowHeight;
  @service('aditools-site') siteconfig;

  get displayWidth() {
    return this.width;
  }
  get isTextHidden() {
    return this.windowHeight < 604;
  }
  get isTextAdjust() {
    return this.windowHeight < 698;
  }
  get scdBranding() {
    let isSCD = this.args.toolConfigData.toolSettings.scdBranding === 'scd'; // Make sure other cases (even invalid) it will be noise
    return {
      icon: isSCD
        ? 'assets/images/aditools-page-svgicons.svg#ea-page-svgicon-signal-chain-designer'
        : 'assets/images/aditools-page-svgicons.svg#ea-page-svgicon-noise',
      title: isSCD ? 'ember-aditools-page.studio.signalchaindesigner' : 'ember-aditools-page.studio.noise',
      url: isSCD ? this.siteconfig.SignalChainDesignerUrl : this.siteconfig.NoiseUrl,
      tooltip: isSCD ? 'tooltips.signalChain' : 'tooltips.noise',
    };
  }
  @action resized(width, height) {
    this.width = width;
    this.height = height;
    this.windowHeight = window.innerHeight;
  }

  <template>
    <div class="tools-body" {{resize this.resized}}>
      <div
        class="tools-heading
          {{if this.isTextAdjust 'tools-heading--adjust'}}
          {{if this.isTextHidden 'tools-heading--hidden'}}"
      >
        <span class="tools-header" data-qa-element="aps_menu_title">
          <svg class="svg-icon" focusable="false"><use
              xlink:href="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-precision-studio"
            ></use></svg>
          {{t "tools.select-tool"}}
        </span>
      </div>
      <div class="tools-panel" width={{this.displayWidth}}>
        <div class="tools-group top-row">
          <CaptionedLink
            @id="btn-noise"
            @icon={{this.scdBranding.icon}}
            @qaName="noise"
            @title={{t this.scdBranding.title}}
            @href={{this.scdBranding.url}}
            @tooltip={{t this.scdBranding.tooltip}}
          />
          <CaptionedLink
            @id="btn-analog-filter"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-analog-filter"
            @qaName="analog_filter"
            @title={{t "ember-aditools-page.studio.filterwizard"}}
            @href={{this.siteconfig.FilterWizardUrl}}
            @tooltip={{t "tooltips.analogFilter"}}
          />
          <CaptionedLink
            @id="btn-in-amp"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-in-amp"
            @qaName="in_amp"
            @title={{t "ember-aditools-page.studio.diamond"}}
            @href={{this.siteconfig.DiamondUrl}}
            @tooltip={{t "tooltips.inAmp"}}
          />
          <CaptionedLink
            @id="btn-photodiode"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-photodiode"
            @qaName="photodiode"
            @title={{t "ember-aditools-page.studio.photodiode"}}
            @href={{this.siteconfig.PhotodiodeUrl}}
            @tooltip={{t "tooltips.photodiode"}}
          />
        </div>
        <div class="tools-group">
          <CaptionedLink
            @id="btn-dac-ebc"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-dac-ebc"
            @qaName="dac-ebc"
            @title={{t "ember-aditools-page.studio.dacebc"}}
            @href={{this.siteconfig.DACErrorCalculatorUrl}}
            @tooltip={{t "tooltips.dacEbc"}}
          />
          <CaptionedLink
            @id="btn-adc-driver"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-adc-driver"
            @qaName="adc_driver"
            @title={{t "ember-aditools-page.studio.adcdriver"}}
            @href={{this.siteconfig.AdcDriverUrl}}
            @tooltip={{t "tooltips.adcDriver"}}
          />
          <CaptionedLink
            @id="btn-dds"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-dds"
            @qaName="dds"
            @title={{t "ember-aditools-page.studio.simdds"}}
            @href={{this.siteconfig.SimDDSUrl}}
            @tooltip={{t "tooltips.dds"}}
          />
          <CaptionedLink
            @id="btn-virtual-eval"
            @icon="assets/images/aditools-page-svgicons.svg#ea-page-svgicon-virtual-eval"
            @qaName="virtual_eval"
            @title={{t "ember-aditools-page.studio.virteval"}}
            @href={{this.siteconfig.VirtualEvalUrl}}
            @tooltip={{t "tooltips.virtualEval"}}
          />
        </div>
      </div>
    </div>
  </template>
}
